import React, { useState, useEffect, useRef } from 'react';
import { Send, AlertCircle, Music, Star, Mic2, Activity, Zap, Sparkles } from 'lucide-react';

// 自定義鋼琴圖示組件 (用於裝飾)
const PianoIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    stroke="currentColor" 
    strokeWidth="0" 
    className={className}
  >
     <path d="M4 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4zm1 2h1.5v9h-1V4zm2.5 0h1v9h-1V4zm2 0h1v9h-1V4zm2 0h1v9h-1V4zm2 0h1.5v9h-1V4zm2.5 0h1.5v9h-1V4zM4 14h1.5v6H4v-6zm2.5 0h1.5v6h-1.5v-6zm2.5 0h1.5v6H9v-6zm2.5 0h1.5v6h-1.5v-6zm2.5 0h1.5v6H14v-6zm2.5 0h1.5v6h-1.5v-6zm2.5 0h1.5v6H19v-6z"/>
  </svg>
);

const WakenMusicForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    services: [],
    satisfied: '',
    reason: '',
    rating: '',
    feedback: '',
    specific: '',
    recommend: '',
    signature: '',
    otherService: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle, success, error
  const [animateWave, setAnimateWave] = useState(false);
  const [isOtherChecked, setIsOtherChecked] = useState(false); // 獨立控制「其他」的勾選狀態
  const [logoError, setLogoError] = useState(false); // 控制 Logo 圖片載入狀態
  const [errors, setErrors] = useState({}); // 儲存驗證錯誤訊息

  // 建立每個區塊的參考點 (Ref)，用於自動捲動
  const nameRef = useRef(null);
  const servicesRef = useRef(null);
  const satisfiedRef = useRef(null);
  const reasonRef = useRef(null);
  const ratingRef = useRef(null);
  const feedbackRef = useRef(null);
  const specificRef = useRef(null);
  const recommendRef = useRef(null);
  const signatureRef = useRef(null);
  
  const otherInputRef = useRef(null);

  // Google Form Action URL (提交位址)
  const GOOGLE_FORM_ACTION_URL = "https://docs.google.com/forms/d/e/1FAIpQLScW723OEAIh97K9n0DDlFXR1_Vd6fRKmEP0s4YVM-dI9jDTBw/formResponse";

  // Google Form 欄位 ID 對應表
  const ENTRY_IDS = {
    name: "entry.1932150036",
    services: "entry.272569509",
    satisfied: "entry.69951365",
    reason: "entry.1606819247", // 若不滿意 (第2頁)
    rating: "entry.1796870908", // 若滿意 (第3頁)
    feedback: "entry.1574769739", // 若滿意 (第3頁)
    specific: "entry.114964881", // 若滿意 (第3頁)
    recommend: "entry.1203554604", // 若滿意 (第3頁)
    signature: "entry.410051877" // 若滿意 (第3頁)
  };

  const serviceOptions = [
    "錄音", "混音／母帶處理", "編曲", "作曲", 
    "填詞", "人聲調音", "廣告音樂", "影片拍攝及後製"
  ];

  useEffect(() => {
    setAnimateWave(true);
  }, []);

  // 當「其他」輸入框有值時，自動勾選 Checkbox
  useEffect(() => {
    if (formData.otherService) {
      setIsOtherChecked(true);
    }
  }, [formData.otherService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // 清除該欄位的錯誤訊息
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      let updatedServices = [...prev.services];
      if (checked) {
        updatedServices.push(value);
      } else {
        updatedServices = updatedServices.filter(service => service !== value);
      }
      // 驗證服務欄位
      if (errors.services && updatedServices.length > 0) {
         setErrors(prev => ({ ...prev, services: null }));
      }
      return { ...prev, services: updatedServices };
    });
  };

  // 處理「其他」選項的 Checkbox 點擊邏輯
  const handleOtherCheckboxClick = (e) => {
    const checked = e.target.checked;
    setIsOtherChecked(checked);
    if (checked) {
      // 勾選時，自動聚焦到輸入框
      setTimeout(() => otherInputRef.current?.focus(), 0);
    } else {
      // 取消勾選時，清空內容
      setFormData(prev => ({ ...prev, otherService: '' }));
    }
  };

  // 表單驗證與提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 1. 建立錯誤物件
    const newErrors = {};
    
    // 必填檢查
    if (!formData.name.trim()) newErrors.name = "請輸入您的名字";
    if (formData.services.length === 0 && !formData.otherService) newErrors.services = "請至少選擇一項服務";
    if (!formData.satisfied) newErrors.satisfied = "請選擇滿意度";

    if (formData.satisfied === "否") {
      if (!formData.reason.trim()) newErrors.reason = "請填寫不滿意的原因";
    } else if (formData.satisfied === "是") {
      if (!formData.rating) newErrors.rating = "請給予評分";
      if (!formData.feedback.trim()) newErrors.feedback = "請填寫體驗及感想";
      if (!formData.specific.trim()) newErrors.specific = "請填寫特別滿意之處";
      if (!formData.recommend) newErrors.recommend = "請選擇推薦意願";
      if (!formData.signature.trim()) newErrors.signature = "請填寫公開署名";
    }

    // 更新錯誤狀態
    setErrors(newErrors);

    // 2. 如果有錯誤，自動捲動到第一個錯誤欄位
    // 使用 setTimeout 確保畫面先渲染出錯誤紅框（會改變高度），然後再捲動，這樣才準確
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const errorOrder = ['name', 'services', 'satisfied', 'reason', 'rating', 'feedback', 'specific', 'recommend', 'signature'];
        const firstErrorKey = errorOrder.find(key => newErrors[key]);
        
        const refs = {
          name: nameRef,
          services: servicesRef,
          satisfied: satisfiedRef,
          reason: reasonRef,
          rating: ratingRef,
          feedback: feedbackRef,
          specific: specificRef,
          recommend: recommendRef,
          signature: signatureRef
        };

        if (firstErrorKey && refs[firstErrorKey].current) {
          refs[firstErrorKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100); // 100ms 延遲
      
      return; // 停止提交
    }

    // 3. 驗證通過，開始提交
    setIsSubmitting(true);

    const formBody = new FormData();
    
    // 填入 Google Form 驗證參數
    formBody.append("fbzx", "-7168589348063988506"); 
    formBody.append("fvv", "1"); 

    // 填入基本欄位
    formBody.append(ENTRY_IDS.name, formData.name);
    formBody.append(ENTRY_IDS.satisfied, formData.satisfied);

    // 填入服務項目 (多選)
    formData.services.forEach(service => {
      formBody.append(ENTRY_IDS.services, service);
    });
    // 處理「其他」選項
    if (formData.otherService) {
      formBody.append(ENTRY_IDS.services, `__other_option__`);
      formBody.append(ENTRY_IDS.services + ".other_option_response", formData.otherService);
    }

    // 條件邏輯提交與分頁設定
    if (formData.satisfied === "否") {
      formBody.append(ENTRY_IDS.reason, formData.reason);
      formBody.append("pageHistory", "0,1"); 
    } else if (formData.satisfied === "是") {
      formBody.append(ENTRY_IDS.rating, formData.rating);
      formBody.append(ENTRY_IDS.feedback, formData.feedback);
      formBody.append(ENTRY_IDS.specific, formData.specific);
      formBody.append(ENTRY_IDS.recommend, formData.recommend);
      formBody.append(ENTRY_IDS.signature, formData.signature);
      formBody.append("pageHistory", "0,2"); 
    }

    try {
      await fetch(GOOGLE_FORM_ACTION_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: formBody
      });
      
      setSubmitStatus('success');
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Submission error", error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 提交成功頁面
  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#111618] flex items-center justify-center p-4 text-white font-sans">
        {/* 背景光暈 */}
        <div className="fixed inset-0 z-0">
           <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] bg-[#236373]/30 rounded-full blur-[100px]"></div>
           <div className="absolute bottom-[-20%] right-[-20%] w-[80%] h-[80%] bg-[#2eaebf]/20 rounded-full blur-[100px]"></div>
        </div>

        <div className="bg-[#1a2024]/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center relative z-10">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-tr from-[#236373] to-[#2eaebf] mb-6 shadow-lg shadow-[#236373]/40">
            <Zap className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-3 tracking-wide">傳送成功</h2>
          <p className="text-gray-300 mb-8 font-light">您的數據已進入 Waken Music 核心資料庫。感謝您的回饋！</p>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-[#236373] to-[#1a4b57] text-white px-4 py-3 rounded-xl font-bold tracking-wider hover:shadow-lg hover:shadow-[#236373]/30 transition duration-300 border border-white/20"
          >
            填寫下一筆
          </button>
        </div>
      </div>
    );
  }

  return (
    // 加入 noValidate 防止瀏覽器預設驗證干擾我們的 JS 驗證
    <div className="min-h-screen bg-[#111618] text-white font-sans selection:bg-[#236373] selection:text-white pb-12 overflow-hidden">
      {/* 背景氛圍 */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[#111618]"></div>
        <div className="absolute top-[-10%] left-1/2 transform -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#236373]/30 to-transparent rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#236373]/20 rounded-full blur-[100px]"></div>
        <div className="absolute top-40 left-[-100px] w-[300px] h-[300px] bg-[#22d3ee]/10 rounded-full blur-[80px]"></div>
        <div className="absolute bottom-20 left-20 w-[200px] h-[200px] bg-purple-500/10 rounded-full blur-[60px]"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-12">
        
        {/* 頁首 Header Section */}
        <div className="relative mb-10 text-center animate-fadeIn">
          {/* Logo 容器 */}
          <div className="inline-flex items-center justify-center mb-6 group hover:scale-105 transition-transform duration-500">
             <div className="w-32 h-32 rounded-full bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-[0_0_40px_rgba(35,99,115,0.25)] overflow-hidden">
                {!logoError ? (
                  <img 
                    src="Carlos_music_logoONLY1_500X500.png" 
                    alt="Waken Music Logo" 
                    className="w-full h-full object-cover" 
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <Music className="h-12 w-12 text-[#236373]" /> 
                )}
             </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-50 to-gray-400 mb-4 drop-shadow-sm">
            Waken Music
          </h1>
          <div className="flex items-center justify-center gap-2">
            <span className="h-[1px] w-8 bg-gradient-to-r from-transparent to-[#236373]/80"></span>
            <p className="text-[#4fd1c5] text-sm tracking-[0.3em] uppercase font-bold">顧客回饋</p>
            <span className="h-[1px] w-8 bg-gradient-to-l from-transparent to-[#236373]/80"></span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" noValidate>
          
          {/* 第一部分：基本資料 (Section 1) */}
          <div className="bg-[#1a2024]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl relative overflow-hidden group">
            {/* 微光澤效果 */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

            <div className="space-y-8 relative z-10">
              {/* 名字 */}
              <div ref={nameRef}>
                <label className="block text-xl font-medium text-gray-200 mb-3 flex items-center gap-3">
                  {/* 修改：顏色改為與 Services 一樣的青色 (#22d3ee) */}
                  <div className="w-1.5 h-6 bg-[#22d3ee] rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]"></div>
                  你的名字 <span className="text-[#236373] text-lg">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full bg-[#0a0c0e]/60 border rounded-xl px-5 py-4 text-lg text-white placeholder-gray-500 focus:bg-[#0a0c0e]/80 outline-none transition-all duration-300
                    ${errors.name 
                      ? 'border-red-500 focus:border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                      : 'border-white/10 focus:border-[#236373]/80 focus:shadow-[0_0_20px_rgba(35,99,115,0.2)]'}`}
                  placeholder="輸入您的稱呼"
                />
                {errors.name && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.name}</p>}
              </div>

              {/* 服務項目 */}
              <div ref={servicesRef}>
                <label className="block text-xl font-medium text-gray-200 mb-4 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-[#22d3ee] rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]"></div>
                  使用的服務 <span className="text-[#236373] text-lg">*</span>
                </label>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 p-1 rounded-xl ${errors.services ? 'border border-red-500/50' : ''}`}>
                  {serviceOptions.map((option) => (
                    <label key={option} className="relative flex items-center p-3.5 rounded-lg border border-white/10 cursor-pointer bg-[#0a0c0e]/40 hover:bg-[#236373]/10 hover:border-[#236373]/50 transition-all duration-300 group/item">
                      <input
                        type="checkbox"
                        value={option}
                        checked={formData.services.includes(option)}
                        onChange={handleCheckboxChange}
                        className="w-5 h-5 rounded border-gray-500 bg-transparent text-[#236373] focus:ring-[#236373]/50 focus:ring-offset-0 accent-[#236373]"
                      />
                      <span className="ml-3 text-gray-300 group-hover/item:text-white transition-colors text-lg">{option}</span>
                    </label>
                  ))}
                  
                  {/* 「其他」選項 */}
                  <label 
                    className="relative flex items-center p-3.5 rounded-lg border border-white/10 cursor-pointer bg-[#0a0c0e]/40 hover:bg-[#236373]/10 hover:border-[#236373]/50 transition-all duration-300 sm:col-span-2 group/item"
                    onClick={(e) => {
                      if (e.target.tagName !== 'INPUT') {
                         if (!isOtherChecked) {
                           setIsOtherChecked(true);
                           setTimeout(() => otherInputRef.current?.focus(), 0);
                         }
                      }
                    }}
                  >
                     <input
                        type="checkbox"
                        checked={isOtherChecked}
                        onChange={handleOtherCheckboxClick}
                        className="w-5 h-5 rounded border-gray-500 bg-transparent text-[#236373] focus:ring-[#236373]/50 focus:ring-offset-0 accent-[#236373] flex-shrink-0"
                      />
                    <span className="ml-3 text-gray-300 whitespace-nowrap text-lg flex-shrink-0 group-hover/item:text-white">其他：</span>
                    <input
                      ref={otherInputRef}
                      type="text"
                      name="otherService"
                      value={formData.otherService}
                      onChange={handleInputChange}
                      onFocus={() => setIsOtherChecked(true)}
                      className="ml-2 w-full bg-transparent border-b border-gray-600 focus:border-[#236373] outline-none text-white text-lg pb-1 transition-colors placeholder-gray-600"
                      placeholder="請輸入服務內容"
                    />
                  </label>
                </div>
                {errors.services && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.services}</p>}
              </div>

              {/* 滿意度確認 */}
              <div ref={satisfiedRef}>
                <label className="block text-xl font-medium text-gray-200 mb-4 flex items-center gap-3">
                  {/* 左側光條顏色調整為與服務一致的青色 */}
                  <div className="w-1.5 h-6 bg-[#22d3ee] rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]"></div>
                  滿意度確認 <span className="text-[#236373] text-lg">*</span>
                </label>
                <div className={`flex gap-4 p-1 rounded-xl ${errors.satisfied ? 'border border-red-500/50' : ''}`}>
                  {['是', '否'].map((option) => (
                    <label key={option} className={`flex-1 relative p-5 rounded-xl border cursor-pointer transition-all duration-300 flex items-center justify-center gap-3 overflow-hidden
                      ${formData.satisfied === option 
                        ? (option === '是' 
                            ? 'bg-gradient-to-br from-cyan-900/30 to-blue-900/40 border-cyan-400/60 shadow-[0_0_20px_rgba(34,211,238,0.3)]' 
                            : 'bg-zinc-700/40 border-zinc-500 shadow-[0_0_20px_rgba(161,161,170,0.2)]')
                        : 'bg-[#0a0c0e]/40 border-white/10 hover:bg-white/5 hover:border-white/30'
                      }`}>
                      <input
                        type="radio"
                        name="satisfied"
                        value={option}
                        checked={formData.satisfied === option}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      {option === '是' ? 
                        <Star className={`h-6 w-6 ${formData.satisfied === '是' ? 'text-cyan-400 fill-cyan-400' : 'text-gray-400'}`} /> : 
                        <AlertCircle className={`h-6 w-6 ${formData.satisfied === '否' ? 'text-zinc-300' : 'text-gray-400'}`} />
                      }
                      <span className={`text-xl font-bold tracking-wide ${formData.satisfied === option ? 'text-white' : 'text-gray-400'}`}>
                        {option === '是' ? '滿意' : '不滿意'}
                      </span>
                    </label>
                  ))}
                </div>
                 {errors.satisfied && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.satisfied}</p>}
              </div>
            </div>
          </div>

          {/* 條件分歧：若不滿意 (Section 2) - 中性灰色調 */}
          {formData.satisfied === "否" && (
            <div className="bg-[#1a2024]/90 backdrop-blur-xl border border-zinc-600/50 rounded-2xl p-6 md:p-8 animate-fadeIn shadow-xl" ref={reasonRef}>
              <h3 className="text-xl font-bold text-zinc-100 mb-6 flex items-center gap-3">
                <div className="p-2 bg-zinc-700/50 rounded-lg">
                  <Activity className="h-5 w-5 text-zinc-300" />
                </div>
                感謝你的反饋
              </h3>
              
              <label className="block text-lg font-medium text-gray-200 mb-3">
                  請告訴我們原因，我們將持續改進服務： <span className="text-[#236373] text-lg">*</span>
              </label>
              
              <div className="bg-[#0a0c0e]/50 rounded-xl p-1 border border-white/10">
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={5}
                  className={`w-full bg-transparent border-0 rounded-lg p-4 text-white focus:ring-0 placeholder-gray-600 outline-none resize-none leading-relaxed text-lg
                     ${errors.reason ? 'border border-red-500/50' : ''}`}
                  placeholder="請輸入您的意見..."
                />
              </div>
              {errors.reason && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.reason}</p>}
            </div>
          )}

          {/* 條件分歧：若滿意 (Section 3) */}
          {formData.satisfied === "是" && (
            <div className="bg-[#1a2024]/90 backdrop-blur-xl border border-[#236373]/30 rounded-2xl p-6 md:p-8 space-y-8 animate-fadeIn relative overflow-hidden">
               {/* 裝飾背景：右側改為鋼琴圖示 */}
               <div className="absolute top-0 right-0 p-6 opacity-20 pointer-events-none">
                 <PianoIcon className="h-32 w-32 text-[#236373] opacity-50 rotate-12" />
               </div>

              <div className="bg-gradient-to-r from-[#236373]/30 to-[#2eaebf]/20 rounded-xl p-4 border border-[#236373]/30 flex items-center gap-4 relative z-10">
                <div className="p-2 bg-[#236373]/20 rounded-lg">
                  <Mic2 className="h-6 w-6 text-[#4fd1c5]" />
                </div>
                <span className="text-cyan-50 font-medium tracking-wide text-lg">感謝您的肯定，我們會為你持續帶來更好的服務。</span>
              </div>

              {/* 服務評分 (1-5) */}
              <div className="relative z-10" ref={ratingRef}>
                <label className="block text-xl font-medium text-white mb-6 flex items-center gap-2">
                  1-5分你會俾幾多分呢？ <span className="text-[#236373] text-lg">*</span>
                </label>
                <div className={`flex justify-between items-center bg-[#0a0c0e]/60 rounded-2xl p-6 border px-4 sm:px-12 relative overflow-hidden
                   ${errors.rating ? 'border-red-500/50' : 'border-white/10'}`}>
                  
                  {/* Dopamine Effect Background for 5 Stars */}
                  {formData.rating === '5' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#236373]/20 to-transparent animate-pulse pointer-events-none"></div>
                  )}

                  {[1, 2, 3, 4, 5].map((num) => (
                    <label key={num} className="cursor-pointer group relative active:scale-75 transition-transform duration-150">
                      <input
                        type="radio"
                        name="rating"
                        value={num}
                        checked={formData.rating === String(num)}
                        onChange={handleInputChange}
                        className="peer hidden"
                      />
                      
                      {/* Star Wrapper - 5th Star Special Animation (Continuous Ring Glow + Pop) */}
                      <div className={`transition-all duration-300 ease-out
                        ${formData.rating === '5' && num === 5 
                          ? 'scale-125 drop-shadow-[0_0_15px_rgba(35,99,115,0.8)]' // Popped and glowing
                          : 'scale-100'}
                      `}>
                        {/* 5th Star Glow Ring - Continuous Pulse */}
                        {num === 5 && formData.rating === '5' && (
                           <div className="absolute inset-0 -m-2 rounded-full border-2 border-[#236373]/50 animate-ping blur-sm opacity-50"></div>
                        )}
                        {/* 5th Star Glow Ring - Static Base */}
                        {num === 5 && formData.rating === '5' && (
                           <div className="absolute inset-0 -m-2 rounded-full border border-[#236373]/30 blur-sm"></div>
                        )}
                        
                        <Star 
                          className={`w-10 h-10 transition-all duration-300 relative z-10
                            ${parseInt(formData.rating) >= num 
                              ? 'text-[#236373] fill-[#236373] drop-shadow-[0_0_5px_rgba(35,99,115,0.4)]' 
                              : 'text-gray-600 group-hover:text-[#236373]/50'
                            }
                            ${num === 5 && formData.rating === '5' ? 'text-[#4fd1c5] fill-[#4fd1c5] animate-pulse' : ''}
                          `} 
                          strokeWidth={1.5}
                        />
                      </div>
                    </label>
                  ))}
                </div>
                {errors.rating && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.rating}</p>}
              </div>

              {/* 體驗及感想 */}
              <div className="group relative z-10" ref={feedbackRef}>
                <label className="block text-lg font-medium text-gray-200 mb-3">
                  請分享使用服務後的體驗及感想 (30-50字為佳) <span className="text-[#236373] text-lg">*</span>
                </label>
                <textarea
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full bg-[#0a0c0e]/60 border rounded-xl px-5 py-4 text-white focus:border-[#236373]/80 focus:bg-[#0a0c0e]/80 focus:shadow-[0_0_20px_rgba(35,99,115,0.15)] outline-none transition-all duration-300 text-lg placeholder-gray-600
                    ${errors.feedback ? 'border-red-500' : 'border-white/10'}`}
                  placeholder="您的體驗對我們很重要..."
                />
                 {errors.feedback && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.feedback}</p>}
              </div>

              {/* 特別滿意之處 */}
              <div className="group relative z-10" ref={specificRef}>
                <label className="block text-lg font-medium text-gray-200 mb-3">
                  你對Waken Music服務的特別滿意之處 (30-50字為佳) <span className="text-[#236373] text-lg">*</span>
                </label>
                <textarea
                  name="specific"
                  value={formData.specific}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full bg-[#0a0c0e]/60 border rounded-xl px-5 py-4 text-white focus:border-[#22d3ee]/50 focus:bg-[#0a0c0e]/80 focus:shadow-[0_0_20px_rgba(34,211,238,0.15)] outline-none transition-all duration-300 text-lg placeholder-gray-600
                     ${errors.specific ? 'border-red-500' : 'border-white/10'}`}
                  placeholder="例如：專業度、效率、溝通、成品等"
                />
                {errors.specific && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.specific}</p>}
              </div>

              {/* 推薦意願 */}
              <div className="relative z-10" ref={recommendRef}>
                <label className="block text-lg font-medium text-gray-200 mb-4">
                  Waken Music的服務值得推薦給朋友嗎 <span className="text-[#236373] text-lg">*</span>
                </label>
                <div className={`flex gap-4 rounded-xl ${errors.recommend ? 'border border-red-500/50 p-1' : ''}`}>
                  {['值得', '不值得'].map((option) => (
                    <label key={option} className={`flex-1 p-4 rounded-xl border cursor-pointer text-center transition-all duration-500
                      ${formData.recommend === option 
                        ? (option === '值得' 
                           ? 'bg-[#236373]/30 border-[#236373] text-white shadow-[0_0_25px_rgba(35,99,115,0.5)] scale-[1.02]' // Glow for "Worth it"
                           : 'bg-[#236373]/30 border-[#236373] text-white shadow-[0_0_20px_rgba(35,99,115,0.3)]')
                        : 'bg-[#0a0c0e]/40 border-white/10 text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}>
                      <input
                        type="radio"
                        name="recommend"
                        value={option}
                        checked={formData.recommend === option}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className="font-bold tracking-wide text-lg">{option}</span>
                    </label>
                  ))}
                </div>
                 {errors.recommend && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.recommend}</p>}
              </div>

              {/* 公開署名 */}
              <div className="group relative z-10" ref={signatureRef}>
                <label className="block text-lg font-medium text-gray-200 mb-2">
                  公開署名 <span className="text-[#236373] text-lg">*</span>
                </label>
                <p className="text-sm text-gray-500 mb-3 ml-1">填 X先生／小姐 或 英文名即可</p>
                <input
                  type="text"
                  name="signature"
                  value={formData.signature}
                  onChange={handleInputChange}
                  className={`w-full bg-[#0a0c0e]/60 border rounded-xl px-5 py-4 text-white focus:border-purple-500/50 focus:bg-[#0a0c0e]/80 focus:shadow-[0_0_15px_rgba(168,85,247,0.15)] outline-none transition-all duration-300 text-lg placeholder-gray-600
                     ${errors.signature ? 'border-red-500' : 'border-white/10'}`}
                  placeholder="例如：陳先生 或 Carlos"
                />
                {errors.signature && <p className="text-red-400 text-sm mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> {errors.signature}</p>}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-8 pb-12 relative">
             {/* 錯誤提示氣泡 */}
             {Object.keys(errors).length > 0 && (
                <div className="absolute top-0 left-0 w-full flex justify-center -mt-4 mb-4">
                  <div className="bg-red-500/90 text-white px-4 py-2 rounded-full text-sm flex items-center animate-bounce shadow-lg">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    請填寫所有必填欄位
                  </div>
                </div>
             )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full relative group overflow-hidden rounded-xl p-[1px] transition-all duration-300
                ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.01] active:scale-[0.99]'}`}
            >
              {/* Animated Gradient Border using brand color */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#236373] via-[#4fd1c5] to-[#1a4b57] animate-gradient-xy"></div>
              
              <div className="relative bg-[#111618] rounded-xl h-full px-6 py-5 flex items-center justify-center gap-3 group-hover:bg-[#151a1c] transition-colors">
                {isSubmitting ? (
                  <span className="text-gray-300 font-mono tracking-widest animate-pulse text-sm">TRANSMITTING...</span>
                ) : (
                  <>
                    <span className="text-white font-bold text-xl tracking-widest uppercase group-hover:text-cyan-50 transition-colors">
                      確認及提交
                    </span>
                    <Send className="h-6 w-6 text-[#236373] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="bg-red-900/20 border border-red-500/30 text-red-200 px-6 py-4 rounded-xl flex items-center gap-3 animate-shake">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <span>訊號中斷，請檢查連線後重試。</span>
            </div>
          )}

        </form>
        
        {/* Footer */}
        <div className="text-center pb-8 opacity-60">
           <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#236373]/40 to-transparent mb-6"></div>
           <p className="text-[12px] font-mono tracking-widest text-gray-400">
             Waken Music Customer Survey 2026
           </p>
        </div>
      </div>
    </div>
  );
};

export default WakenMusicForm;
